<template>
    <div>
        <h2>{{ $t("settings.channels.Channel setup") }}</h2>

        <div>
            <h3>{{ $t("settings.channels.Identity configuration") }}</h3>
            <b-form-group
                id="fieldset-identity"
                :label="$t('settings.channels.identity.label')"
                content-cols-lg="7"
                content-cols-sm
                label-cols-lg="3"
                label-cols-sm="4"
                label-for="identity"
            >
                {{ formatIdentity(channelData.identity) }}

                <b-button v-b-tooltip.hover.top="$t('Copy')" class="btn-icon ml-50" variant="light" @click="doCopy">
                    <font-awesome-icon icon="copy" />
                </b-button>
            </b-form-group>

            <b-form-group
                v-if="
                    (channelData.platform.toLowerCase() === 'whatsapp' &&
                        channelData.provider.toLowerCase() === 'messagebird') ||
                    channelData.platform.toLowerCase() === 'test'
                "
                id="fieldset-templateNamespace"
                :description="$t('settings.channels.template-namespace.description')"
                :label="$t('settings.channels.template-namespace.label')"
                content-cols-lg="7"
                content-cols-sm
                label-cols-lg="3"
                label-cols-sm="4"
                label-for="identity"
            >
                <b-form-input
                    id="input-template-namespace"
                    v-model="getSettingByName('templateNamespace').value"
                    type="text"
                />
            </b-form-group>
        </div>

        <AppSms v-if="channelData.platform.toLowerCase() === 'sms'" :channel-data="channelData" />

        <!-- specific auth components for channels which connect through a button, all others get a generic auth with fields to fill in -->
        <!-- email doesn't need auth but has special settings needs so we also added that to this if -->
        <AppTwitterAuth v-if="channelData.provider.toLowerCase() === 'twitter'" :channel-data="channelData" />
        <!--        <AppEazyAuth-->
        <!--            v-else-if="channelData.provider.toLowerCase() === 'eazy'"-->
        <!--            :channel-data="channelData"-->
        <!--        />-->
        <AppFacebookAuth v-else-if="channelData.provider.toLowerCase() === 'facebook'" :channel-data="channelData" />
        <AppEmail v-else-if="channelData.platform.toLowerCase() === 'email'" :channel-data="channelData" />
        <AppTelegram v-else-if="channelData.platform.toLowerCase() === 'telegram'" :channel-data="channelData" />
        <AppGenericAuth v-else :authentication-columns="authenticationColumns" :channel-data="channelData" />
        <!-- end of if -->

        <h3>{{ $t("settings.channels.auto-archive.title") }}</h3>

        <b-form-group
            id="fieldset-autoArchive"
            :description="$t('settings.channels.auto-archive.description')"
            :label="$t('settings.channels.auto-archive.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="name"
        >
            <b-form-select
                v-model="getSettingByName('autoArchive').value"
                :options="autoArchiveOptions"
                name="autoArchive"
            />
        </b-form-group>

        <b-form-group
            id="fieldset-autoArchive-message-direction"
            :description="$t('settings.channels.auto-archive.message-direction.description')"
            :label="$t('settings.channels.auto-archive.message-direction.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="autoArchiveMessageDirections"
        >
            <b-form-select
                v-model="getSettingByName('autoArchiveMessageDirection').value"
                :options="autoArchiveMessageDirections"
                name="autoArchiveMessageDirections"
            />
        </b-form-group>
        <hr />
    </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required } from "@validations";
import { BCard, BButton, BFormGroup, BFormInput, BFormSelect, VBTooltip } from "bootstrap-vue";
import useChannelSettings from "../useChannelSettings";
import AppGenericAuth from "./auth/generic";
import AppTwitterAuth from "./auth/twitter";
import AppEazyAuth from "./auth/eazy";
import AppFacebookAuth from "./auth/facebook";
import AppTelegram from "./auth/telegram";
import AppEmail from "./emailSettings";
import AppSms from "@/views/modules/settings/channels/components/smsSettings";
import { useToast } from "vue-toastification/composition";
import i18n from "@/libs/i18n";

export default {
    components: {
        BButton,
        BFormGroup,
        BCard,
        BFormInput,
        ValidationProvider,
        ValidationObserver,
        BFormSelect,
        AppGenericAuth,
        AppTwitterAuth,
        AppEazyAuth,
        AppFacebookAuth,
        AppTelegram,
        AppEmail,
        AppSms,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        channelData: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            required,
            autoArchiveOptions: [
                {
                    value: "false",
                    text: i18n.t("settings.channels.auto-archive.never"),
                },
                {
                    value: '{ "weeks": 1 }',
                    text: i18n.tc("settings.channels.auto-archive.week", 1),
                },
                {
                    value: '{ "weeks": 2 }',
                    text: i18n.tc("settings.channels.auto-archive.week", 2),
                },
                {
                    value: '{ "weeks": 3 }',
                    text: i18n.tc("settings.channels.auto-archive.week", 3),
                },
                {
                    value: '{ "months": 1 }',
                    text: i18n.tc("settings.channels.auto-archive.month", 1),
                },
                {
                    value: '{ "months": 3 }',
                    text: i18n.tc("settings.channels.auto-archive.month", 3),
                },
                {
                    value: '{ "months": 6 }',
                    text: i18n.tc("settings.channels.auto-archive.month", 6),
                },
            ],
            autoArchiveMessageDirections: [
                {
                    value: "out",
                    text: i18n.t("settings.channels.auto-archive.message-direction.outbound"),
                },
                {
                    value: "in",
                    text: i18n.t("settings.channels.auto-archive.message-direction.inbound"),
                },
                {
                    value: "both",
                    text: i18n.t("settings.channels.auto-archive.message-direction.both"),
                },
            ],
        };
    },
    methods: {
        doCopy() {
            this.$copyText(this.formatIdentity(this.channelData.identity)).then(
                () => {
                    this.toast.success(this.$t("copied"));
                },
                () => {
                    this.toast.error(this.$t("not-copied"));
                }
            );
        },
        formatIdentity(identity) {
            if (this.channelData.platform.toLowerCase() === "email") {
                const customerName = window.location.hostname.split(".").find((subdomain) => subdomain !== "www");
                return `${customerName}.${identity}@${process.env.VUE_APP_MAILGUN_DOMAIN}`;
            }
            return identity;
        },
    },
    setup(props) {
        const { authenticationColumns, getSettingByName } = useChannelSettings(props.channelData);
        const toast = useToast();

        return {
            authenticationColumns,
            getSettingByName,

            toast,
        };
    },
};
</script>
